import { Link } from '@tanstack/react-router';
import { Button } from 'app/javascript/components/ui/button';
import WhatsAppIcon from 'app/javascript/components/store/WhatsAppIcon';
import { ROUTES } from 'app/javascript/constants/routes';
import { trackEvent } from 'app/javascript/firebase';
import type { StoreProduct } from 'app/javascript/data/storeCatalogs';

interface ProductBuyButtonProps {
  product: StoreProduct;
  // wa.me deep link; null when the public fetch is still loading (loading=true)
  // or the company has no usable phone.
  whatsappUrl: string | null;
  loading: boolean;
  companySlug: string;
  // Where this button is rendered, carried into analytics so we can tell which
  // surface drives the WhatsApp conversion.
  source: 'card' | 'lightbox' | 'detail';
  // When set, the button is disabled and shows this label instead of the CTA —
  // used to require a size selection before buying.
  disabledReason?: string | null;
  className?: string;
}

// The product buy action: WhatsApp CTA when a number is available, a loading
// state while the fetch resolves, a "blocked" state when a prerequisite is
// missing (e.g. no size selected), and a "contact the academy" fallback when
// there is no usable number (R7). Owns the conversion-funnel analytics (the
// WhatsApp click is the goal event).
export default function ProductBuyButton({ product, whatsappUrl, loading, companySlug, source, disabledReason, className }: ProductBuyButtonProps) {
  const baseParams = {
    company_slug: companySlug,
    product: product.id,
    category: product.category,
    source,
  };

  if (loading) {
    return (
      <Button
        disabled
        variant="outline"
        className={`h-12 w-full border-slate-700 bg-slate-900 text-slate-400 shadow-none ${className ?? ''}`}
      >
        <WhatsAppIcon className="size-4" />
        Carregando...
      </Button>
    );
  }

  if (disabledReason) {
    return (
      <Button
        disabled
        variant="outline"
        className={`h-12 w-full border-dashed border-slate-700 bg-slate-900 text-slate-500 shadow-none ${className ?? ''}`}
      >
        {disabledReason}
      </Button>
    );
  }

  if (whatsappUrl) {
    return (
      <Button
        asChild
        className={`h-12 w-full bg-yellow-400 font-bebas text-lg uppercase tracking-wide text-slate-900 shadow-lg shadow-yellow-400/20 transition-all hover:bg-yellow-300 hover:shadow-xl hover:shadow-yellow-400/30 ${className ?? ''}`}
      >
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent('store_whatsapp_clicked', baseParams)}
        >
          <WhatsAppIcon className="size-5" />
          Comprar
        </a>
      </Button>
    );
  }

  return (
    <Button
      asChild
      variant="outline"
      className={`h-12 w-full border-slate-700 bg-slate-900 text-slate-300 shadow-none hover:border-slate-600 hover:bg-slate-800/70 hover:text-foreground ${className ?? ''}`}
    >
      <Link
        to={ROUTES.COMPANY_DETAILS}
        params={{ slug: companySlug }}
        onClick={() => trackEvent('store_contact_fallback_clicked', baseParams)}
      >
        Ver contato da academia
      </Link>
    </Button>
  );
}
